import React from "react";
import { AlertTriangle, Calendar, CheckCircle2, FileText, MapPin, X } from "lucide-react";
import { cn } from "../utils";
import type { CastMember, ScheduleImportIssue, ScheduledScene, ShootDay, ShootingSchedule } from "../types";

type Props = {
  draft: ShootingSchedule;
  onClose: () => void;
  onChange: (draft: ShootingSchedule) => void;
  onConfirm: () => void;
};

const updateCastMember = (draft: ShootingSchedule, castId: string, patch: Partial<CastMember>) => ({
  ...draft,
  castMembers: draft.castMembers.map((member) => (member.id === castId ? { ...member, ...patch, updatedAt: new Date().toISOString() } : member)),
  updatedAt: new Date().toISOString(),
});

const updateShootDay = (draft: ShootingSchedule, dayId: string, patch: Partial<ShootDay>) => ({
  ...draft,
  shootDays: draft.shootDays.map((day) => (day.id === dayId ? { ...day, ...patch, updatedAt: new Date().toISOString() } : day)),
  updatedAt: new Date().toISOString(),
});

const updateScheduledScene = (draft: ShootingSchedule, sceneId: string, patch: Partial<ScheduledScene>) => ({
  ...draft,
  scheduledScenes: draft.scheduledScenes.map((scene) => (scene.id === sceneId ? { ...scene, ...patch, updatedAt: new Date().toISOString() } : scene)),
  updatedAt: new Date().toISOString(),
});

const updateIssue = (draft: ShootingSchedule, issueId: string, patch: Partial<ScheduleImportIssue>) => ({
  ...draft,
  issues: draft.issues.map((issue) => (issue.id === issueId ? { ...issue, ...patch, updatedAt: new Date().toISOString() } : issue)),
  updatedAt: new Date().toISOString(),
});

export function ShootingScheduleImportModal({ draft, onClose, onChange, onConfirm }: Props) {
  const scheduledScenes = draft.scheduledScenes.filter((scene) => !scene.isOmitted && !scene.isUnscheduled);
  const unscheduledScenes = draft.scheduledScenes.filter((scene) => scene.isUnscheduled || scene.isOmitted);
  const unresolvedIssues = draft.issues.filter((issue) => !issue.resolved);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/50 p-6">
      <div className="flex max-h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <Calendar className="h-4 w-4" />
              Shooting Schedule Review
            </div>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">{draft.title}</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Review the extracted shoot days, cast, and scenes before saving this schedule into the project.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-3 border-b border-slate-200 px-6 py-4 md:grid-cols-5 dark:border-slate-800">
          {[
            { label: "Shoot Days", value: draft.shootDays.length },
            { label: "Scenes", value: scheduledScenes.length },
            { label: "Omitted / Unscheduled", value: unscheduledScenes.length },
            { label: "Cast Members", value: draft.castMembers.length },
            { label: "Open Issues", value: unresolvedIssues.length },
          ].map((item) => (
            <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">{item.label}</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">{item.value}</div>
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-auto px-6 py-5">
          <div className="space-y-8">
            <section>
              <div className="mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-indigo-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cast Members</h3>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                  <thead className="bg-slate-50 dark:bg-slate-950">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Code</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Character</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
                    {draft.castMembers.map((member) => (
                      <tr key={member.id}>
                        <td className="px-4 py-3">
                          <input
                            value={member.castCode}
                            onChange={(event) => onChange(updateCastMember(draft, member.id, { castCode: event.target.value }))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={member.characterName}
                            onChange={(event) => onChange(updateCastMember(draft, member.id, { characterName: event.target.value }))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={member.type}
                            onChange={(event) => onChange(updateCastMember(draft, member.id, { type: event.target.value as CastMember["type"] }))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-indigo-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Shoot Days</h3>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                  <thead className="bg-slate-50 dark:bg-slate-950">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Day</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Call</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
                    {draft.shootDays.map((day) => (
                      <tr key={day.id}>
                        <td className="px-4 py-3">
                          <input
                            value={day.shootDayNumber ?? ""}
                            onChange={(event) =>
                              onChange(updateShootDay(draft, day.id, { shootDayNumber: event.target.value ? Number(event.target.value) : null }))
                            }
                            className="w-24 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={day.shootDate}
                            onChange={(event) => onChange(updateShootDay(draft, day.id, { shootDate: event.target.value }))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={`${day.callTimeStart || ""}${day.callTimeEnd ? ` - ${day.callTimeEnd}` : ""}`}
                            onChange={(event) => {
                              const [start, end] = event.target.value.split("-").map((value) => value.trim());
                              onChange(updateShootDay(draft, day.id, { callTimeStart: start || null, callTimeEnd: end || null }));
                            }}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={day.dayType}
                            onChange={(event) => onChange(updateShootDay(draft, day.id, { dayType: event.target.value as ShootDay["dayType"] }))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <textarea
                            value={day.dayNotes.join("\n")}
                            onChange={(event) => onChange(updateShootDay(draft, day.id, { dayNotes: event.target.value.split("\n").map((item) => item.trim()).filter(Boolean) }))}
                            rows={2}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-indigo-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Scheduled Scenes</h3>
              </div>
              <div className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                  <thead className="bg-slate-50 dark:bg-slate-950">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Scene</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Shoot Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Heading</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Location</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Cast</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500">Bid Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
                    {draft.scheduledScenes.map((scene) => {
                      const shootDay = draft.shootDays.find((day) => day.id === scene.shootDayId);
                      return (
                        <tr key={scene.id}>
                          <td className="px-4 py-3">
                            <input
                              value={scene.originalSceneNumber || ""}
                              onChange={(event) => onChange(updateScheduledScene(draft, scene.id, { originalSceneNumber: event.target.value }))}
                              className="w-32 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                            />
                          </td>
                          <td className="px-4 py-3 text-slate-600">{shootDay?.shootDate || "Unscheduled"}</td>
                          <td className="px-4 py-3">
                            <input
                              value={scene.sceneHeadingRaw || scene.description || ""}
                              onChange={(event) => onChange(updateScheduledScene(draft, scene.id, { sceneHeadingRaw: event.target.value }))}
                              className="w-full min-w-[240px] rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              value={scene.locationName || scene.locationRaw || ""}
                              onChange={(event) => onChange(updateScheduledScene(draft, scene.id, { locationName: event.target.value }))}
                              className="w-full min-w-[180px] rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              value={scene.castCodes.join(", ")}
                              onChange={(event) =>
                                onChange(
                                  updateScheduledScene(draft, scene.id, {
                                    castCodes: event.target.value.split(",").map((item) => item.trim()).filter(Boolean),
                                    castCodesRaw: event.target.value,
                                  }),
                                )
                              }
                              className="w-full min-w-[160px] rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                            />
                          </td>
                          <td className="px-4 py-3">
                            {scene.linkedSequenceTitle || scene.linkedSceneId ? (
                              <span className="inline-flex rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                                {scene.linkedSequenceTitle || "Linked"}
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">
                                Review
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Import Issues</h3>
              </div>
              <div className="space-y-3">
                {draft.issues.map((issue) => (
                  <label
                    key={issue.id}
                    className={cn(
                      "flex items-start gap-3 rounded-xl border px-4 py-3",
                      issue.resolved ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={issue.resolved}
                      onChange={(event) => onChange(updateIssue(draft, issue.id, { resolved: event.target.checked }))}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-[#ffcc01] focus:ring-[#ffcc01]"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{issue.message}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {issue.severity}
                        </span>
                      </div>
                      {issue.rawText ? <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{issue.rawText}</div> : null}
                    </div>
                  </label>
                ))}
              </div>
            </section>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            Confirming this import will save the reviewed shooting schedule into the project calendar.
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="rounded-lg bg-[#ffcc01] px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-[#e6b801]"
            >
              Confirm Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
